import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useNotification } from '../../context/NotificationContext';
import { useTranslation } from 'react-i18next';
import {
  ClockIcon, TruckIcon, CheckIcon, EyeIcon, 
  XCircleIcon, DocumentArrowDownIcon,
  MapPinIcon, UserIcon, CalendarIcon,
  CreditCardIcon, TagIcon, GlobeAltIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import { 
  SparklesIcon, 
  ArrowPathIcon,
  ShoppingBagIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/solid';
import api from '../../api/client';
import { BadgeDollarSign, Package, ShieldCheck, Truck, RefreshCw, AlertCircle } from 'lucide-react';

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { orders, fetchOrders, loading, error } = useCart();
  const { notify } = useNotification();
  const { t } = useTranslation();

  // States for button loading and UI
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (user) fetchOrders();
  }, [user, ]);

  const order = orders.find((o) => o.id === Number(id));

  // Refresh order data
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchOrders();
      notify('success', t('orderDetailPage.refresh.success'));
    } catch (err) {
      notify('error', t('orderDetailPage.refresh.error'));
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle cancel order with better conditions
  const handleCancelOrder = async () => {
    if (!user || !order) return;
    
    // Check if order can be cancelled
    const canCancel = order.status === 'pending' || order.status === 'confirmed';
    
    if (!canCancel) {
      notify('error', t('orderDetailPage.cancel.notAllowed'));
      return;
    }
    
    // Double confirmation for safety
    if (!cancelConfirm) {
      setCancelConfirm(true);
      // Auto reset after 5 seconds
      setTimeout(() => {
        setCancelConfirm(false);
      }, 5000);
      return;
    }
    
    setIsCancelling(true);
    try {
      await api.patch(`/orders/${order.id}/`, { status: 'cancelled' });
      await fetchOrders();
      notify('success', t('orderDetailPage.cancel.success'));
      setCancelConfirm(false);
      
      // Success animation
      const button = document.querySelector('[data-button="cancel"]');
      if (button) {
        button.classList.add('action-success');
        setTimeout(() => {
          button.classList.remove('action-success');
        }, 2000);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || t('orderDetailPage.cancel.error');
      notify('error', errorMessage);
    } finally {
      setIsCancelling(false);
    }
  };

  // Handle download bill
  const handleDownloadBill = async () => {
    if (!order || !id || isDownloading) return;
    
    setIsDownloading(true);
    try {
      const response = await api.get<Blob>(`/orders/${id}/bill/`, { 
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${t('orderDetailPage.bill.fileName')}_${id}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Trigger download with animation
      setTimeout(() => {
        link.click();
        // Download success animation
        const button = document.querySelector('[data-button="download"]');
        if (button) {
          button.classList.add('download-success');
          setTimeout(() => {
            button.classList.remove('download-success');
          }, 2000);
        }
      }, 300);
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setIsDownloading(false);
      }, 1000);
      
      notify('success', t('orderDetailPage.bill.downloadSuccess'));
    } catch (err: any) {
      setIsDownloading(false);
      const errorMessage = err.response?.data?.message || t('orderDetailPage.bill.downloadError');
      notify('error', errorMessage);
    }
  };

  // Handle payment
  const handlePayOrder = () => {
    if (!order?.payment_url || isProcessingPayment) return;
    
    setIsProcessingPayment(true);
    
    const button = document.querySelector('[data-button="pay"]');
    if (button) {
      button.classList.add('processing');
    }
    
    setTimeout(() => {
      window.open(order.payment_url, '_blank');
      
      setTimeout(() => {
        setIsProcessingPayment(false);
        if (button) {
          button.classList.remove('processing');
        }
      }, 1000);
    }, 500);
  };

  // Define status steps with proper translations
  const statusSteps = [
    { 
      name: 'pending', 
      label: t('orderDetailPage.status.pending'), 
      icon: <ClockIcon className="h-6 w-6" />,
      color: 'bg-amber-100 text-amber-700 border-amber-300',
      description: t('orderDetailPage.statusDescriptions.pending')
    },
    { 
      name: 'confirmed', 
      label: t('orderDetailPage.status.confirmed'), 
      icon: <CheckIcon className="h-6 w-6" />,
      color: 'bg-blue-100 text-blue-700 border-blue-300',
      description: t('orderDetailPage.statusDescriptions.confirmed')
    },
    { 
      name: 'processing', 
      label: t('orderDetailPage.status.processing'), 
      icon: <ArrowPathIcon className="h-6 w-6" />,
      color: 'bg-indigo-100 text-indigo-700 border-indigo-300',
      description: t('orderDetailPage.statusDescriptions.processing')
    },
    { 
      name: 'shipped', 
      label: t('orderDetailPage.status.shipped'), 
      icon: <Truck className="h-6 w-6" />,
      color: 'bg-purple-100 text-purple-700 border-purple-300',
      description: t('orderDetailPage.statusDescriptions.shipped')
    },
    { 
      name: 'delivered', 
      label: t('orderDetailPage.status.delivered'), 
      icon: <Package className="h-6 w-6" />,
      color: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      description: t('orderDetailPage.statusDescriptions.delivered')
    },
    { 
      name: 'cancelled', 
      label: t('orderDetailPage.status.cancelled'), 
      icon: <XCircleIcon className="h-6 w-6" />,
      color: 'bg-rose-100 text-rose-700 border-rose-300',
      description: t('orderDetailPage.statusDescriptions.cancelled')
    },
    { 
      name: 'refunded', 
      label: t('orderDetailPage.status.refunded'), 
      icon: <RefreshCw className="h-6 w-6" />,
      color: 'bg-gray-100 text-gray-700 border-gray-300',
      description: t('orderDetailPage.statusDescriptions.refunded')
    },
  ];

  // Find current status step
  const currentStep = statusSteps.findIndex((step) => step.name === order?.status?.toLowerCase());

  // Determine if order can be cancelled
  const canCancel = order && (order.status === 'pending' || order.status === 'confirmed');
  
  // Determine if payment button should be shown
  const canPay = order && 
                 (order.status === 'pending' || order.status === 'confirmed') && 
                 order.payment_url && 
                 order.payment_status === 'unpaid';

  // Determine if download is available
  const canDownload = order && order.status !== 'pending';

  // Render cancellation status
  const renderCancellationStatus = () => {
    if (order?.status === 'cancelled') {
      return (
        <div className="mt-6 p-6 bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-rose-100 rounded-xl">
              <XCircleIcon className="h-6 w-6 text-rose-600" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-rose-800">{t('orderDetailPage.cancellation.title')}</h4>
              <p className="text-sm text-rose-600">{t('orderDetailPage.cancellation.description')}</p>
            </div>
          </div>
          {/* {order.cancelled_at && (
            <div className="mt-3 pt-3 border-t border-rose-100">
              <p className="text-sm text-rose-500">
                <span className="font-medium">{t('orderDetailPage.cancellation.cancelledAt')}:</span> {new Date(order.cancelled_at).toLocaleString()}
              </p>
            </div>
          )} */}
          <button
            onClick={() => window.location.href = '/support?type=cancelled_order'}
            className="mt-4 w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-200"
          >
            {t('orderDetailPage.buttons.contactSupport')}
          </button>
        </div>
      );
    }
    
    if (order?.payment_status === 'refunded') {
      return (
        <div className="mt-6 p-6 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-emerald-100 rounded-xl">
              <RefreshCw className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-emerald-800">{t('orderDetailPage.refund.title')}</h4>
              <p className="text-sm text-emerald-600">{t('orderDetailPage.refund.description')}</p>
            </div>
          </div>
          <p className="text-xs text-emerald-500 mt-3">
            {t('orderDetailPage.refund.note')}
          </p>
        </div>
      );
    }
    
    return null;
  };

  // Render order status information
  const renderStatusInfo = () => {
    const currentStatus = statusSteps.find(step => step.name === order?.status?.toLowerCase());
    if (!currentStatus) return null;

    return (
      <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <div className="p-3 bg-blue-100 rounded-xl ">
            {currentStatus.name === 'delivered' ? (
              <Package className="h-6 w-6 text-blue-600" />
            ) : currentStatus.name === 'shipped' ? (
              <Truck className="h-6 w-6 text-blue-600" />
            ) : currentStatus.name === 'cancelled' ? (
              <XCircleIcon className="h-6 w-6 text-rose-600" />
            ) : (
              <CheckIcon className="h-6 w-6 text-blue-600" />
            )}
          </div>
          <div>
            <h4 className="text-lg font-bold text-blue-800">{currentStatus.label}</h4>
            <p className="text-sm text-blue-600">{currentStatus.description}</p>
          </div>
        </div>
        
        {order?.status === 'shipped' || order?.status !== 'cancelled' && (
          <div className="mt-3 pt-3 border-t border-blue-100">
            <p className="text-sm text-blue-600">
              {/* <span className="font-medium">{t('orderDetailPage.summary.estimatedDelivery')}:</span> {new Date(order.estimated_delivery).toLocaleDateString()} */}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Render warning for pending orders
  const renderPendingWarning = () => {
    if (order?.status === 'pending' || order?.status !== 'cancelled') {
      return (
        <div className="mb-6 p-6 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-amber-100 rounded-xl">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-amber-800">{t('orderDetailPage.warnings.pending.title')}</h4>
              <p className="text-sm text-amber-600">{t('orderDetailPage.warnings.pending.description')}</p>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            <p className="text-xs text-amber-500 flex items-center gap-2">
              <CheckIcon className="h-3 w-3" />
              {t('orderDetailPage.warnings.pending.tip1')}
            </p>
            <p className="text-xs text-amber-500 flex items-center gap-2">
              <CheckIcon className="h-3 w-3" />
              {t('orderDetailPage.warnings.pending.tip2')}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Render order items
  const renderOrderItems = () => {
    if (!order?.items || order.items.length === 0) return null;

    return (
      <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden">
        <div className="p-8">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3 mb-6">
            <ShoppingBagIcon className="h-6 w-6 text-blue-500" />
            {t('orderDetailPage.items.title')} ({order.items.length})
          </h2>
          
          <div className="space-y-4">
            {order.items.map((item: any, index: number) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
                    {item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <ShoppingBagIcon className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800">{item.name}</h4>
                    <p className="text-sm text-gray-500">SKU: {item.sku || 'N/A'}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm font-medium text-blue-600">
                        {t('orderDetailPage.currencySymbol')}{item.price}
                      </span>
                      <span className="text-gray-400">×</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                        {item.quantity}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-800">
                    {t('orderDetailPage.currencySymbol')}{(item.price * item.quantity).toFixed(2)}
                  </p>
                  {item.discount > 0 && (
                    <p className="text-sm text-emerald-600">
                      {t('orderDetailPage.items.saved')}: {t('orderDetailPage.currencySymbol')}{item.discount}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render customer contact info
  const renderCustomerContact = () => {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
          <div className="p-2 bg-blue-100 rounded-lg">
            <UserIcon className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('orderDetailPage.summary.customer')}</p>
            <p className="font-medium">{order?.customer?.username || 'N/A'}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
          <div className="p-2 bg-green-100 rounded-lg">
            <PhoneIcon className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('orderDetailPage.summary.mobile')}</p>
            <p className="font-medium">{order?.address?.mobile || order?.address?.mobile || 'N/A'}</p>
          </div>
        </div>
      </div>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-8">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto shadow-lg mb-4">
              <UserIcon className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {t('orderDetailPage.auth.loginRequired')}
            </h2>
            <p className="text-gray-600">{t('orderDetailPage.auth.loginPrompt')}</p>
          </div>
          <Link 
            to="/login" 
            className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
          >
            {t('orderDetailPage.auth.loginLink')}
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="relative">
          <div className="w-24 h-24 border-4 border-gray-200 rounded-full animate-spin border-t-blue-500 border-r-purple-500 border-b-pink-500"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <ShoppingBagIcon className="h-10 w-10 text-blue-500 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!order || error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-gradient-to-r from-rose-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircleIcon className="h-12 w-12 text-rose-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            {t('orderDetailPage.errors.notFound')}
          </h2>
          <p className="text-gray-600 mb-8">
            {error || t('orderDetailPage.errors.orderNotFound')}
          </p>
          <div className="flex gap-4 justify-center">
            <Link 
              to="/orders" 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
            >
              <ArrowPathIcon className="h-5 w-5" />
              {t('orderDetailPage.navigation.backToOrders')}
            </Link>
            <button
              onClick={handleRefresh}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
            >
              <RefreshCw className="h-5 w-5" />
              {t('orderDetailPage.buttons.refresh')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
    
      
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header with Refresh Button */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-lg">
                  <ShoppingBagIcon className="h-6 w-6 text-white" />
                </div>
                <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  {t('orderDetailPage.title')} 
                  <span className="ml-2 text-blue-600">#{order.uuid || order.id}</span>
                </h1>
              </div>
              <p className="text-gray-600 flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                {new Date(order.created_at).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRefreshing ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>{t('orderDetailPage.buttons.refreshing')}</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    <span>{t('orderDetailPage.buttons.refresh')}</span>
                  </>
                )}
              </button>
              
              <div className={`px-4 py-2 rounded-full font-semibold shadow-sm ${
                order.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' :
                order.status === 'cancelled' ? 'bg-rose-100 text-rose-700' :
                order.payment_status === 'refunded' ? 'bg-gray-100 text-gray-700' :
                order.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {t(`orderDetailPage.status.${order.status.toLowerCase()}`)}
              </div>
            </div>
          </div>

          {/* Status Information & Warnings */}
          {renderStatusInfo()}
          {renderPendingWarning()}
          {renderCancellationStatus()}
        </div>

        {/* Progress Timeline */}
        {order.status !== 'cancelled' && (
          <div className="mb-10">
            <div className="relative">
              <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200 rounded-full z-0 "></div>
              <div 
                className="absolute top-6 left-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full z-10 transition-all duration-1000 ease-out"
                style={{ width: `${(currentStep / (statusSteps.length - 1)) * 100}%` }}
              ></div>
              
              <div className="relative flex justify-between z-20 max-sm:flex-wrap">
                {statusSteps.map((step, index) => (
                  <div key={step.name} className="flex flex-col items-center">
                    <div className={`relative mb-4 ${
                      index <= currentStep 
                        ? 'scale-110 transform transition-transform duration-300' 
                        : ''
                    }`}>
                      <div className={`w-14 h-14 rounded-full border-4 shadow-lg flex items-center justify-center transition-all duration-300 ${
                        index <= currentStep 
                          ? step.color + ' border-white shadow-xl' 
                          : 'bg-white text-gray-400 border-gray-200'
                      }`}>
                        {step.icon}
                      </div>
                      {index <= currentStep && (
                        <div className="absolute -top-1 -right-1">
                          <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
                            <CheckIcon className="h-3 w-3 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                    <span className={`text-sm font-medium text-center max-w-24 ${
                      index <= currentStep ? 'text-gray-800' : 'text-gray-500'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Order Info & Items */}
          <div className="lg:col-span-2 space-y-8">
            {/* Order Summary Card */}
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden">
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                    <SparklesIcon className="h-6 w-6 text-yellow-500" />
                    {t('orderDetailPage.summary.title')}
                  </h2>
                  <div className="flex items-center gap-2 text-lg font-bold text-gray-800">
                    <TagIcon className="h-5 w-5" />
                    {t('orderDetailPage.currencySymbol')}{Number(order.total).toFixed(2)}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Customer Contact Info */}
                  {renderCustomerContact()}

                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CreditCardIcon className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('orderDetailPage.summary.paymentMethod')}</p>
                        <p className="font-medium capitalize">{order.payment_method || t('orderDetailPage.summary.notAvailable')}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <ShieldCheck className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('orderDetailPage.summary.paymentStatus')}</p>
                        <p className={`font-medium capitalize ${
                          order.payment_status === 'paid' ? 'text-emerald-600' : 
                          order.payment_status === 'refunded' ? 'text-gray-600' : 
                          'text-amber-600'
                        }`}>
                          {t(`orderDetailPage.payment.${order.payment_status?.toLowerCase() ?? 'na'}`)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <ClockIcon className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t('orderDetailPage.summary.lastUpdated')}</p>
                        <p className="font-medium">
                          {new Date(order.updated_at).toLocaleString([], { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-8 pt-6 border-t border-gray-200 flex flex-wrap gap-4">
                  {/* Download Button */}
                  {canDownload && (
                    <button
                      data-button="download"
                      onClick={handleDownloadBill}
                      disabled={isDownloading}
                      className="flex-1 min-w-[200px] group relative overflow-hidden bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isDownloading ? (
                        <>
                          <div className="relative w-5 h-5">
                            <div className="absolute inset-0 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          </div>
                          <span className="relative z-10">
                            {t('orderDetailPage.buttons.downloading')}...
                          </span>
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 animate-pulse"></div>
                        </>
                      ) : (
                        <>
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
                          <DocumentArrowDownIcon className="h-5 w-5 relative z-10" />
                          <span className="relative z-10">{t('orderDetailPage.buttons.downloadBill')}</span>
                        </>
                      )}
                    </button>
                  )}

                  {/* Cancel Button */}
                  {canCancel && (
                    <button
                      data-button="cancel"
                      onClick={handleCancelOrder}
                      disabled={isCancelling}
                      className={`flex-1 min-w-[200px] group relative overflow-hidden ${
                        cancelConfirm 
                          ? 'bg-gradient-to-r from-red-600 to-red-700' 
                          : 'bg-gradient-to-r from-rose-500 to-pink-600'
                      } text-white px-6 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed`}
                    >
                      {isCancelling ? (
                        <>
                          <div className="relative w-5 h-5">
                            <div className="absolute inset-0 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          </div>
                          <span className="relative z-10">
                            {t('orderDetailPage.buttons.cancelling')}...
                          </span>
                          <div className="absolute inset-0 bg-gradient-to-r from-rose-600 to-pink-700 animate-pulse"></div>
                        </>
                      ) : cancelConfirm ? (
                        <>
                          <div className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-800 translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
                          <XCircleIcon className="h-5 w-5 relative z-10" />
                          <span className="relative z-10">{t('orderDetailPage.buttons.confirmCancel')}</span>
                        </>
                      ) : (
                        <>
                          <div className="absolute inset-0 bg-gradient-to-r from-rose-600 to-pink-700 translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
                          <XCircleIcon className="h-5 w-5 relative z-10" />
                          <span className="relative z-10">{t('orderDetailPage.buttons.cancelOrder')}</span>
                        </>
                      )}
                    </button>
                  )}

                  {/* Pay Button */}
                  {canPay && (
                    <button
                      data-button="pay"
                      onClick={handlePayOrder}
                      disabled={isProcessingPayment}
                      className="flex-1 min-w-[200px] group relative overflow-hidden bg-gradient-to-r from-emerald-500 to-green-600 text-white px-6 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isProcessingPayment ? (
                        <>
                          <div className="relative w-5 h-5">
                            <div className="absolute inset-0 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          </div>
                          <span className="relative z-10">
                            {t('orderDetailPage.buttons.processingPayment')}...
                          </span>
                          <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-green-700 animate-pulse"></div>
                        </>
                      ) : (
                        <>
                          <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-green-700 translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
                          <BadgeDollarSign className="h-5 w-5 relative z-10" />
                          <span className="relative z-10">{t('orderDetailPage.buttons.payNow')}</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Order Items */}
            {renderOrderItems()}

            {/* Delivery Address Card */}
            {order.address && order.status !== 'cancelled' && (
              <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden">
                <div className="p-8">
                  <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3 mb-6">
                    <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                      <MapPinIcon className="h-6 w-6 text-white" />
                    </div>
                    {t('orderDetailPage.address.title')}
                  </h2>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      {[
                        { icon: GlobeAltIcon, label: t('orderDetailPage.address.city'), value: order.address.city, color: 'blue' },
                        { icon: MapPinIcon, label: t('orderDetailPage.address.street'), value: order.address.street, color: 'purple' },
                        { icon: UserIcon, label: t('orderDetailPage.address.neighborhood'), value: order.address.neighborhood, color: 'pink' },
                      ].map((item, index) => (
                        <div key={index} className="flex items-start gap-3 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                          <div className={`p-2 bg-${item.color}-100 rounded-lg`}>
                            <item.icon className={`h-5 w-5 text-${item.color}-600`} />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">{item.label}</p>
                            <p className="font-medium">{item.value || t('orderDetailPage.summary.notAvailable')}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-4">
                      {[
                        { icon: TagIcon, label: t('orderDetailPage.address.compound'), value: order.address.compound, color: 'emerald' },
                        { icon: ShoppingBagIcon, label: t('orderDetailPage.address.building'), value: order.address.building_or_house, color: 'amber' },
                        { icon: PhoneIcon, label: t('orderDetailPage.address.mobile'), value: order.address.mobile, color: 'indigo' },
                      ].map((item, index) => (
                        <div key={index} className="flex items-start gap-3 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                          <div className={`p-2 bg-${item.color}-100 rounded-lg`}>
                            <item.icon className={`h-5 w-5 text-${item.color}-600`} />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">{item.label}</p>
                            <p className="font-medium">{item.value || t('orderDetailPage.summary.notAvailable')}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {order.address.latitude && order.address.longitude && (
                    <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                      <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                        <MapPinIcon className="h-4 w-4" />
                        <strong>{t('orderDetailPage.address.coordinates')}:</strong>
                      </p>
                      <div className="flex items-center gap-4">
                        <code className="text-sm bg-white/80 px-3 py-2 rounded-lg font-mono">
                          {order.address.latitude.toFixed(6)}, {order.address.longitude.toFixed(6)}
                        </code>
                        <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                          {t('orderDetailPage.address.viewOnMap')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Status & Timeline */}
          <div className="space-y-8">
            {/* Order Timeline */}
            {order.status !== 'cancelled' && (
              <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <ClockIcon className="h-5 w-5 text-gray-600" />
                  {t('orderDetailPage.timeline.title')}
                </h3>
                <div className="space-y-6">
                  <div className="relative pl-8 pb-6">
                    <div className="absolute left-3 top-1 w-0.5 h-full bg-gradient-to-b from-blue-400 to-purple-400"></div>
                    <div className="absolute left-0 top-1 w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full border-4 border-white shadow"></div>
                    <div>
                      <p className="font-semibold text-gray-800">{t('orderDetailPage.timeline.placed')}</p>
                      <p className="text-sm text-gray-600">{new Date(order.created_at).toLocaleString()}</p>
                    </div>
                  </div>

                  {order.status !== 'pending' && (
                    <div className="relative pl-8 pb-6">
                      <div className="absolute left-3 top-1 w-0.5 h-full bg-gradient-to-b from-blue-400 to-purple-400"></div>
                      <div className="absolute left-0 top-1 w-6 h-6 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full border-4 border-white shadow"></div>
                      <div>
                        <p className="font-semibold text-gray-800">{t('orderDetailPage.timeline.confirmed')}</p>
                        <p className="text-sm text-gray-600">
                          {new Date(order.updated_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}

                  {order.status === 'delivered' && (
                    <div className="relative pl-8">
                      <div className="absolute left-0 top-1 w-6 h-6 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full border-4 border-white shadow"></div>
                      <div>
                        <p className="font-semibold text-gray-800">{t('orderDetailPage.timeline.delivered')}</p>
                        <p className="text-sm text-gray-600">
                          {new Date(order.updated_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Stats */}
            {order.status !== 'cancelled' && (
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white">
                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5" />
                  {t('orderDetailPage.stats.title')}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                    <span>{t('orderDetailPage.stats.itemsCount')}</span>
                    <span className="font-bold text-lg">{order.items?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                    {/* <span>{t('orderDetailPage.stats.shipping')}</span>
                    <span className="font-bold text-lg">
                      {order.shipping_cost ? `${t('orderDetailPage.currencySymbol')}${order.shipping_cost}` : t('orderDetailPage.stats.freeShipping')}
                    </span> */}
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                    {/* <span>{t('orderDetailPage.stats.discount')}</span>
                    <span className="font-bold text-lg">
                      {order.discount ? `${t('orderDetailPage.currencySymbol')}${order.discount}` : t('orderDetailPage.stats.noDiscount')}
                    </span> */}
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                    {/* <span>{t('orderDetailPage.stats.tax')}</span>
                    <span className="font-bold text-lg">
                      {order.tax ? `${t('orderDetailPage.currencySymbol')}${order.tax}` : t('orderDetailPage.stats.noTax')}
                    </span> */}
                  </div>
                </div>
              </div>
            )}

            {/* Support Card */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-white/10 rounded-lg">
                  <ShieldCheck className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">{t('orderDetailPage.support.title')}</h3>
              </div>
              <p className="text-gray-300 mb-6 text-sm">
                {t('orderDetailPage.support.description')}
              </p>
              <div className="space-y-3 mb-6">
                <a 
                  href="mailto:support@bazzar.com" 
                  className="flex items-center gap-2 text-blue-300 hover:text-blue-200 transition-colors"
                >
                  <span className="text-sm">📧 support@bazzar.com</span>
                </a>
                <a 
                  href="tel:+201234567890" 
                  className="flex items-center gap-2 text-blue-300 hover:text-blue-200 transition-colors"
                >
                  <span className="text-sm">📞 +20 123 456 7890</span>
                </a>
              </div>
              <button 
                onClick={() => window.location.href = '/support'}
                className="w-full bg-white text-gray-900 hover:bg-gray-100 font-semibold py-3 rounded-lg transition-all duration-200 transform hover:-translate-y-0.5"
              >
                {t('orderDetailPage.support.contactButton')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;